import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { ReviewsStateModel } from './reviews.model'
import { ReviewsService } from '../reviews.service'
import {
  GetGoogleReviewsDetail,
  GetGoogleReviewsSummary,
  GetGoogleReviewsTrends,
  GetReviews,
  ReplyReview,
  ReplySugestion
} from './reviews.actions'
import { tap } from 'rxjs'
import { cloneDeep, sortBy } from 'lodash'
import { IReviewShopInfo } from '../../interfaces/reviews.interface'
import {
  IGoogleReviewsDetailResponse,
  IGoogleReviewsSummaryResponse,
  IGoogleReviewsTrendsResponse
} from '../../interfaces/google-reviews.interface'

@State<ReviewsStateModel>({
  name: 'reviews'
})
@Injectable()
export class ReviewsState {
  constructor(private reviewsService: ReviewsService) {}

  @Selector()
  static shopReviews(state: ReviewsStateModel) {
    return state.shopReviews
  }

  @Selector()
  static allShops(state: ReviewsStateModel): IReviewShopInfo[] {
    return (
      state.shopReviews
        ?.map((el) => ({
          locationName: el.locationName,
          locationId: el.locationId
        }))
        .flat() ?? []
    )
  }

  @Selector()
  static replyMessage(state: ReviewsStateModel) {
    return state.replyMessage
  }

  @Selector()
  static googleReviewsSummary(state: ReviewsStateModel): IGoogleReviewsSummaryResponse | null {
    return state.googleReviewsSummary
  }

  @Selector()
  static googleReviewsDetail(state: ReviewsStateModel): IGoogleReviewsDetailResponse | null {
    return state.googleReviewsDetail
  }

  @Selector()
  static googleReviewsTrends(state: ReviewsStateModel): IGoogleReviewsTrendsResponse | null {
    return state.googleReviewsTrends
  }

  @Action(GetReviews)
  getReviews(ctx: StateContext<ReviewsStateModel>) {
    return this.reviewsService.getReviews().pipe(
      tap((res) => {
        ctx.patchState({
          shopReviews: res ? sortBy(res.locations, (x) => x.locationName) : undefined
        })
      })
    )
  }

  @Action(ReplySugestion)
  replySugestion(ctx: StateContext<ReviewsStateModel>, { payload }: ReplySugestion) {
    return this.reviewsService.replySugestion(payload).pipe(
      tap((res) => {
        ctx.patchState({
          replyMessage: res?.message
        })
      })
    )
  }

  @Action(GetGoogleReviewsSummary)
  getGoogleReviewsSummary(ctx: StateContext<ReviewsStateModel>, { payload }: GetGoogleReviewsSummary) {
    return this.reviewsService.getGoogleReviewsSummary(payload).pipe(
      tap((res) => {
        if (res?.locations) {
          res.locations = sortBy(res.locations, (x) => x.locationName)
        }
        ctx.patchState({ googleReviewsSummary: res })
      })
    )
  }

  @Action(GetGoogleReviewsDetail)
  getGoogleReviewsDetail(ctx: StateContext<ReviewsStateModel>, { payload }: GetGoogleReviewsDetail) {
    return this.reviewsService.getGoogleReviewsDetail(payload.locationId, payload.from, payload.to).pipe(
      tap((res) => {
        ctx.patchState({ googleReviewsDetail: res })
      })
    )
  }

  @Action(GetGoogleReviewsTrends)
  getGoogleReviewsTrends(ctx: StateContext<ReviewsStateModel>, { payload }: GetGoogleReviewsTrends) {
    return this.reviewsService.getGoogleReviewsTrends(payload).pipe(
      tap((res) => {
        ctx.patchState({ googleReviewsTrends: res })
      })
    )
  }

  @Action(ReplyReview)
  replyReview(ctx: StateContext<ReviewsStateModel>, { payload }: ReplyReview) {
    let shopReviews = cloneDeep(ctx.getState()?.shopReviews ?? [])
    return this.reviewsService.replyReview(payload).pipe(
      tap(() => {
        const index = shopReviews.findIndex((el) => el.locationId === payload.locationId)
        const shop = shopReviews[index]
        shop.reviews = shop.reviews.filter((el) => el.reviewId !== payload.reviewId)
        if (shop.reviews.length < 1) {
          shopReviews = shopReviews.filter((el) => el.locationId !== payload.locationId)
        } else {
          shopReviews[index] = shop
        }

        ctx.patchState({
          shopReviews
        })
      })
    )
  }
}
